import React from 'react';
import { Box, Divider, InputAdornment, Stack, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Timelapse } from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Order } from '@api/Orders/Order';
import RangeDatePicker from '@components/Utilities/DatePicker/RangeDatePicker';
import IOSSwitch from '@components/Utilities/Switch/IOSSwitch';
import OrderLabelName from '@screens/Orders/components/OrderLabelName';
import OrdersViewSwitch, { OrdersView } from '@screens/Orders/components/OrdersViewSwitch';
import { EmptyListState } from '@screens/Orders/components/OrderEmptyStates';
import { OrdersDateRange } from '@screens/Orders/hooks/useOrdersFeed';

interface OrdersListColumnProps {
    orders: Order[];
    currentOrder?: Order;
    onSelectOrder: (order: Order) => void;
    search: string;
    onChangeSearch: (search: string) => void;
    view: OrdersView;
    onChangeView: (view: OrdersView) => void;
    filters: OrdersDateRange;
    /** Updater funcional: dos cambios seguidos (inicio y fin) no se pisan. */
    onChangeFilters: React.Dispatch<React.SetStateAction<OrdersDateRange>>;
    hideTodayLongTime: boolean;
    onToggleHideTodayLongTime: () => void;
    /** `Capability.HideLongRunningOrders`: lo decide la pantalla con `can()`. */
    canHideLongRunning: boolean;
}

/**
 * La columna izquierda de la vista Lista: filtros de pantalla (buscador, vista,
 * rango de fechas, ocultar los de mucho tiempo) y la lista de pedidos.
 * Presentacional: los filtros viven en `useOrdersFeed`.
 */
const OrdersListColumn: React.FunctionComponent<OrdersListColumnProps> = ({
    orders,
    currentOrder,
    onSelectOrder,
    search,
    onChangeSearch,
    view,
    onChangeView,
    filters,
    onChangeFilters,
    hideTodayLongTime,
    onToggleHideTodayLongTime,
    canHideLongRunning
}) => (
    <Stack height="100%">
        <Stack p={4} spacing={2}>
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Buscar pedido"
                value={search}
                onChange={(e) => onChangeSearch(e.target.value)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <SearchIcon />
                        </InputAdornment>
                    )
                }}
            />
            <OrdersViewSwitch fullWidth view={view} onChange={onChangeView} />
            <Stack direction="row" spacing={1} alignItems="center">
                <Box flex={1}>
                    <RangeDatePicker
                        fullWidth
                        value={format(filters.startDate, 'dd MMMM, yyy', { locale: es })}
                        onChangeStartDate={(startDate) => onChangeFilters((current) => ({ ...current, startDate }))}
                        onChangeEndDate={(endDate) => onChangeFilters((current) => ({ ...current, endDate }))}
                        onChangeRange={(startDate, endDate) => onChangeFilters((current) => ({ ...current, startDate, endDate }))}
                    />
                </Box>
                <Timelapse color={hideTodayLongTime ? 'success' : 'inherit'} />
                {canHideLongRunning && <IOSSwitch checked={hideTodayLongTime} onChange={onToggleHideTodayLongTime} />}
            </Stack>
        </Stack>
        <Divider />
        {orders.length === 0 ? (
            <EmptyListState />
        ) : (
            orders.map((order, index) => (
                <Stack key={index}>
                    <OrderLabelName
                        order={order}
                        isSelected={currentOrder?.id === order.id}
                        onSelectOrder={onSelectOrder}
                    />
                    <Divider />
                </Stack>
            ))
        )}
    </Stack>
);

export default OrdersListColumn;
